// One rep's pay, as someone else reads it: a supervisor under My reps (/reps/$repId/...)
// or a pay viewer under People (/people/$userId/..., ADR 0028). The routes gate the pages
// and find the rep's name; these pages frame the shared views in the caller's breadcrumb.
import { Breadcrumb, Button, formatDate } from '@hkdevs/design-system';
import { useTenant } from '../../api/queries/tenant';
import { PageLayout } from '../../shell/Page';
import { PAGES, type PageKey } from '../../shell/pages';
import { RouterLink } from '../../shell/RouterLink';
import { CheckDetailView, checkTitle } from '../checkDetail/CheckDetailView';
import { DownloadStatement } from '../checkDetail/DownloadStatement';
import { PayHistoryView, type PayHistorySearch } from '../payHistory/PayHistoryView';
import { PendingPayView, type PendingPaySearch } from '../pendingPay/PendingPayView';
import { CopySearchLink } from '../search/CopySearchLink';
import { ExportLineItems } from '../search/ExportLineItems';
import {
  LineItemSearchView,
  type LineItemSearchParams as LineItemSearch,
} from '../search/LineItemSearchView';

/** Where a rep's pay pages sit, and what the caller knows about the rep so far. */
export interface RepPayFrame {
  /** The page the nav marks: My reps, or People. */
  page: PageKey;
  /** The breadcrumb's first link: My reps, or People (with its filters). */
  root: { label: string; href: string };
  /** The rep's Pay History, the breadcrumb's second link. */
  repHref: string;
  /** The rep's name, once it has loaded. */
  name: string | undefined;
}

const crumbs = (frame: RepPayFrame, last: string) => (
  <Breadcrumb
    items={[
      { label: frame.root.label, href: frame.root.href, as: RouterLink },
      ...(frame.name ? [{ label: frame.name, href: frame.repHref, as: RouterLink }] : []),
      { label: last },
    ]}
  />
);

/**
 * The rep's Pay History, with Search line items when the tenant has search, and Pending
 * pay when `onOpenPending` is given (People; My reps reaches it from its Pending view).
 */
export function RepPayHistoryPage({
  frame,
  repId,
  search,
  onSearchChange,
  onOpenCheck,
  onOpenSearch,
  onOpenPending,
}: {
  frame: RepPayFrame;
  repId: string;
  search: PayHistorySearch;
  onSearchChange: (next: PayHistorySearch) => void;
  onOpenCheck: (payDate: string) => void;
  onOpenSearch: () => void;
  onOpenPending?: (() => void) | undefined;
}) {
  const { data: tenant } = useTenant();
  const searchOn = tenant?.features.includes('line_item_search') ?? false;
  const actions = [
    onOpenPending && (
      <Button key="pending" variant="secondary" leadingIcon="calendar" onClick={onOpenPending}>
        Pending pay
      </Button>
    ),
    searchOn && (
      <Button key="search" variant="secondary" leadingIcon="search" onClick={onOpenSearch}>
        Search line items
      </Button>
    ),
  ].filter(Boolean);
  return (
    <PageLayout
      page={frame.page}
      title={frame.name ?? 'Pay History'}
      subtitle="Pay History"
      actions={actions.length > 0 ? actions : undefined}
      breadcrumb={
        <Breadcrumb
          items={[
            { label: frame.root.label, href: frame.root.href, as: RouterLink },
            { label: frame.name ?? '…' },
          ]}
        />
      }
    >
      <PayHistoryView
        key={repId}
        repId={repId}
        search={search}
        onSearchChange={onSearchChange}
        onOpenCheck={onOpenCheck}
      />
    </PageLayout>
  );
}

/** One of the rep's checks, with its statement download. */
export function RepCheckPage({
  frame,
  repId,
  payDate,
}: {
  frame: RepPayFrame;
  repId: string;
  /** `YYYY-MM-DD`. */
  payDate: string;
}) {
  return (
    <PageLayout
      page={frame.page}
      title={checkTitle(payDate)}
      subtitle={frame.name ?? ''}
      actions={<DownloadStatement payDate={payDate} repId={repId} />}
      breadcrumb={crumbs(frame, formatDate(payDate))}
    >
      <CheckDetailView key={`${repId}/${payDate}`} payDate={payDate} repId={repId} />
    </PageLayout>
  );
}

/** The rep's pending pay. */
export function RepPendingPage({
  frame,
  repId,
  search,
  onSearchChange,
}: {
  frame: RepPayFrame;
  repId: string;
  search: PendingPaySearch;
  onSearchChange: (next: PendingPaySearch) => void;
}) {
  return (
    <PageLayout
      page={frame.page}
      title={frame.name ? `${frame.name} · Pending pay` : 'Pending pay'}
      subtitle="Checks awaiting processing"
      breadcrumb={crumbs(frame, 'Pending pay')}
    >
      <PendingPayView key={repId} repId={repId} search={search} onSearchChange={onSearchChange} />
    </PageLayout>
  );
}

/** The rep's line items, with the PDF export. */
export function RepSearchPage({
  frame,
  repId,
  search,
  onSearchChange,
}: {
  frame: RepPayFrame;
  repId: string;
  search: LineItemSearch;
  onSearchChange: (next: LineItemSearch) => void;
}) {
  return (
    <PageLayout
      page={frame.page}
      title={frame.name ? `${frame.name} · Search` : 'Search'}
      subtitle={PAGES.paySearch.subtitle}
      actions={
        <>
          <CopySearchLink search={search} />
          <ExportLineItems repId={repId} search={search} />
        </>
      }
      breadcrumb={crumbs(frame, 'Search')}
    >
      <LineItemSearchView
        key={repId}
        repId={repId}
        search={search}
        onSearchChange={onSearchChange}
      />
    </PageLayout>
  );
}
